import React, { Component } from 'react';
import { func, bool, object, string, array } from 'prop-types';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import {
  Button,
  ProfileIcon,
  ShieldCheckIcon,
} from '@snapask/common-components';

import {
  getShowUserCompletedProfileDialog,
  getAllowDeleteAccount,
  getAccountStatusError,
  getAccountStatus,
  hideUserCompletedProfileDialog,
} from '@dragonfruit/redux/profile';
import {
  fetchTutorAuditInfo,
  getUnfinishedCertificated,
} from '@dragonfruit/redux/tutorAudit';
import {
  editAccount,
  editAccountComplete,
  editAccountInitialize,
  closeEditPanel,
} from '@dragonfruit/redux/editAccount';
import { coconut, deleteAccount } from '@dragonfruit/redux/navigation';
import {
  showAlertBanner,
  hideAlertBanner,
} from '@dragonfruit/redux/alertBanner';

import { STUDENT, TUTOR } from '@dragonfruit/constants/userRoles';
import { ASK_QUESTION } from '@dragonfruit/constants/hashTags';
import { ALERT_ERROR_THEME } from '@dragonfruit/constants/alertBannerThemes';

import { yearMonthDayFormat } from '@dragonfruit/utils/dateParser';

import AccountProfileVerify from './components/AccountProfileVerify';
import AccountProfileResetPassword from './components/AccountProfileResetPassword';
import AccountProfileBankInfo from './components/AccountProfileBankInfo';
import AccountTestsCard from './components/AccountTestsCard';
import EditAccountForm from './components/EditAccountForm';
import CompletedProfileDialog from './components/CompletedProfileDialog';
import BasicInfo from './components/BasicInfo';

import styles from './AccountProfile.scss';

const mapStateToProps = state => ({
  userInfo: state.profile.data,
  editAccountOpen: state.editAccount.editAccountOpen,
  editAccountStatus: state.editAccount.editAccountStatus,
  allowDeleteAccount: getAllowDeleteAccount(state),
  accountStatusError: getAccountStatusError(state),
  showUserCompletedProfileDialog: getShowUserCompletedProfileDialog(state),
  unfinishedCertificates: getUnfinishedCertificated(state),
});

class AccountProfile extends Component {
  static propTypes = {
    closeEditPanel: func.isRequired,
    editAccount: func.isRequired,
    editAccountComplete: func.isRequired,
    editAccountInitialize: func.isRequired,
    editAccountOpen: bool.isRequired,
    fetchTutorAuditInfo: func.isRequired,
    getAccountStatus: func.isRequired,
    goToCoconut: func.isRequired,
    goToDeleteAccount: func.isRequired,
    hideAlertBanner: func.isRequired,
    hideUserCompletedProfileDialog: func.isRequired,
    showAlertBanner: func.isRequired,
    showUserCompletedProfileDialog: bool.isRequired,
    t: func.isRequired,
    accountStatusError: string,
    allowDeleteAccount: bool,
    unfinishedCertificates: array,
    userInfo: object,
  };

  static defaultProps = {
    accountStatusError: '',
    allowDeleteAccount: false,
    userInfo: {},
    editAccountOpen: false,
    editAccountStatus: '',
    unfinishedCertificates: [],
  };

  componentDidMount() {
    const {
      getAccountStatus,
      userInfo: { role },
      fetchTutorAuditInfo,
    } = this.props;

    getAccountStatus();

    if (role === TUTOR) {
      fetchTutorAuditInfo();
    }
  }

  componentWillUnmount() {
    this.props.closeEditPanel();
  }

  onEditAccountSubmit = values => {
    const {
      userInfo: {
        firstName,
        lastName,
        birthday,
        gender,
        username,
        role,
        tutorDescription,
      },
      editAccount,
    } = this.props;

    const data = {};

    if (values.gender && values.gender.value !== gender) {
      data.gender = values.gender.value;
    }

    if (role === STUDENT) {
      if (
        values.yearOfBirth.value &&
        values.monthOfBirth.value &&
        values.dayOfBirth.value
      ) {
        const updatedDob = yearMonthDayFormat(
          `${values.yearOfBirth.value}-${values.monthOfBirth.value}-${values.dayOfBirth.value}`
        );
        const formattedDob = yearMonthDayFormat(birthday);

        if (updatedDob !== formattedDob) {
          data.date_of_birth = updatedDob;
        }
      }
    }

    if (values.firstName !== firstName) {
      data.first_name = values.firstName;
    }

    if (values.lastName !== lastName) {
      data.last_name = values.lastName;
    }

    if (values.username !== username) {
      data.username = values.username;
    }

    if (role === TUTOR && values.tutorDescription !== tutorDescription) {
      data.tutor_introduction = values.tutorDescription;
    }

    editAccount(data, true);
  };

  onToggleEditAccount = evt => {
    if (evt) evt.preventDefault();
    const {
      editAccountOpen,
      editAccountComplete,
      editAccountInitialize,
    } = this.props;

    if (editAccountOpen) {
      editAccountComplete();
      return;
    }

    editAccountInitialize();

    return;
  };

  handleConfirm = () => {
    const { hideUserCompletedProfileDialog, hideAlertBanner } = this.props;
    hideUserCompletedProfileDialog();
    hideAlertBanner();
  };

  handleAskQuestion = () => {
    const { hideUserCompletedProfileDialog, goToCoconut } = this.props;
    hideUserCompletedProfileDialog();
    goToCoconut(ASK_QUESTION);
  };

  handleClickDeleteAccount = () => {
    const {
      allowDeleteAccount,
      accountStatusError,
      goToDeleteAccount,
      showAlertBanner,
    } = this.props;

    if (!allowDeleteAccount) {
      showAlertBanner({
        theme: ALERT_ERROR_THEME,
        message: accountStatusError,
      });

      return;
    }

    goToDeleteAccount();
  };

  render() {
    const {
      t,
      userInfo,
      editAccountOpen,
      showUserCompletedProfileDialog,
    } = this.props;

    const {
      username,
      firstName,
      lastName,
      gender,
      birthday,
      role,
      tutorDescription,
      school,
      extraQuotaByCompletingProfile,
    } = userInfo;

    let yearOfBirth = {
      label: t('account.year'),
      value: '',
    };
    let monthOfBirth = {
      label: t('account.month'),
      value: '',
    };
    let dayOfBirth = {
      label: t('account.day'),
      value: '',
    };

    if (birthday) {
      const birthdayArr = birthday && birthday.split('-');
      yearOfBirth = {
        label: parseInt(birthdayArr[0], 10),
        value: parseInt(birthdayArr[0], 10),
      };
      monthOfBirth = {
        label: parseInt(birthdayArr[1], 10),
        value: parseInt(birthdayArr[1], 10),
      };
      dayOfBirth = {
        label: parseInt(birthdayArr[2], 10),
        value: parseInt(birthdayArr[2], 10),
      };
    }

    return (
      <div className={styles.root}>
        {showUserCompletedProfileDialog && (
          <CompletedProfileDialog
            handleAskQuestion={this.handleAskQuestion}
            handleConfirm={this.handleConfirm}
            quota={extraQuotaByCompletingProfile}
          />
        )}
        <div className={styles.cardContainer}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={styles.text}>
                <ProfileIcon width={40} height={40} />
                <h4>{t('account.accountCardTitle')}</h4>
              </div>
              {!editAccountOpen && (
                <div className={styles.actions}>
                  <Button
                    text={t('account.edit')}
                    onClick={this.onToggleEditAccount}
                    secondary
                  />
                </div>
              )}
            </div>
            {editAccountOpen ? (
              <EditAccountForm
                onSubmit={this.onEditAccountSubmit}
                onToggleEditAccount={this.onToggleEditAccount}
                userRole={role}
                schoolName={school}
                initialValues={{
                  firstName,
                  lastName,
                  username,
                  gender: {
                    label: gender || t('account.gender'),
                    value: gender,
                  },
                  yearOfBirth,
                  monthOfBirth,
                  dayOfBirth,
                  tutorDescription,
                }}
              />
            ) : (
              <BasicInfo {...userInfo} />
            )}
          </div>
        </div>
        <div className={styles.spacer} />
        <div className={styles.cardContainer}>
          <div className={styles.card}>
            <div className={styles.header}>
              <div className={styles.text}>
                <ShieldCheckIcon width={40} height={40} />
                <h4>{t('account.verificationCardTitle')}</h4>
              </div>
            </div>
            <AccountProfileVerify />
          </div>

          <div className={styles.smallPaddingCard}>
            <AccountProfileResetPassword />
          </div>
          {role === TUTOR && (
            <div className={styles.smallPaddingCard}>
              <AccountProfileBankInfo />
            </div>
          )}
          {role === TUTOR && (
            <div className={styles.smallPaddingCard}>
              <AccountTestsCard />
            </div>
          )}

          <div className={styles.smallPaddingCard}>
            <a
              onClick={this.handleClickDeleteAccount}
              className={styles.deleteAccount}
            >
              {t('deleteAccount:profile.deleteAccount')}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default translate(['account', 'error', 'deleteAccount'])(
  connect(mapStateToProps, {
    editAccount,
    editAccountComplete,
    editAccountInitialize,
    closeEditPanel,
    fetchTutorAuditInfo,
    goToCoconut: coconut,
    goToDeleteAccount: deleteAccount,
    showAlertBanner,
    hideAlertBanner,
    getAccountStatus,
    hideUserCompletedProfileDialog,
  })(AccountProfile)
);



// WEBPACK FOOTER //
// ./app/containers/Account/children/AccountProfile/AccountProfile.jsx